(ns dashboard.entity.document.viewer
  "The principle contract of the dashboard.entity component namespaces are this:
     1. All components will NOT depend on keyword references to subs and events in
        external NSes, but rather receive data as arguments, functions and callbacks.
     2. All internal reliance on the re-frame app DB must be internally contained.
     3. All re-frame app subs and events must NOT be directly referenced by outside
        NSes, but rather via GETTER and SETTER functions - the same is true of internal
        calls - prefer using parameterized functions.
     4. The entity is a UI component that may encapsulate low-level business rules on
        UI controls, but is not responsible for hydrating components with data or entrenched
        references to component namespaces. The UI is customizable, and top-level entities
        should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            [dashboard.entity.analysis.search :as analysis.search]
            [dashboard.entity.document.attribution :as attribution]
            [dashboard.entity.document.db :as doc.db]
            [dashboard.entity.document.evidence :as evidence]
            [dashboard.entity.document.js-events :as js.events]
            [dashboard.entity.document.paragraphs :as doc-paragraphs]
            [dashboard.entity.document.pdf :as pdf]
            [dashboard.entity.document.search :as doc-search]
            [dashboard.ui :as ui]
            [re-frame.core :as rf]
            [reagent.core :as r]))

;; ----------------------------------------------------------------------------
;;  Components
;; ----------------------------------------------------------------------------

(defn content-options
  [mode set-mode set-active-highlight document-id threshold attribution on-load-paragraphs]
  (let [evidence-not-ready (not (and (seq (:provider attribution))
                                     (seq (:information-type attribution))
                                     (:expiry-date attribution)))]
    [:div.level-left.my-0
     [:button.button
      {:on-click #(set-mode :attribution)
       :class (if (= mode :attribution) :is-primary :is-tertiary)}
      "Attribution"]
     [:button.button
      {:on-click (fn []
                   (set-mode :evidence)
                   (set-active-highlight nil))
       :disabled evidence-not-ready
       :class (if (= mode :evidence) :is-primary :is-tertiary)}
      "Evidence"]
     [:button.button
      {:on-click #(set-mode :search)
       :class (if (= mode :search) :is-primary :is-tertiary)}
      "Search"]
     [:button.button
      {:on-click (fn []
                   (set-mode :paragraphs)
                   (on-load-paragraphs document-id threshold))
       :disabled evidence-not-ready
       :class (if (= mode :paragraphs) :is-primary :is-tertiary)}
      "Recommended Analysis"]]))

(defn document-viewer-layout
  "Main layout for the pdF viewer."
  [{:keys [active-highlight
           all-analysis
           attribution
           attribution-props
           document-id
           document-search-results
           fetch-file-preview-url
           highlighted
           is-obsolete
           loaded
           loading-paragraphs
           on-create-paragraph-evidence
           on-create-selection-evidence
           on-load-paragraphs
           on-select-search-result
           on-set-document-search-results
           paragraphs
           saving
           search-query
           selected-constructs
           selected-paragraph
           selected-text
           set-active-highlight
           set-pdf-ref
           set-threshold
           threshold]
    :as viewer-props}]
  (let [[mode set-mode] (react/useState (if selected-paragraph
                                          :search
                                          :attribution))
        [selected-construct set-selected-construct] (react/useState nil)

        set-mode (fn [mode]
                   (let [sheets (array-seq (.-styleSheets js/document))]
                     (doseq [sheet sheets]
                       (let [rules (array-seq (.-cssRules sheet))]
                         (doseq [rule rules]
                           (when (= ".highlightLayer" (.-selectorText rule))
                             (set! (.-display (.-style rule))
                                   (if (= mode :paragraphs) "none" "inherit")))))))

                   (let [sheets (array-seq (.-styleSheets js/document))]
                     (doseq [sheet sheets]
                       (let [rules (array-seq (.-cssRules sheet))]
                         (doseq [rule rules]
                           (when (= ".paragraphsLayer" (.-selectorText rule))
                             (set! (.-display (.-style rule))
                                   (if (#{:paragraphs} mode) "inherit" "none")))))))
                   (set-mode mode))]
   [:div.document-viewer
    (if search-query
      [:f> evidence/viewer-analysis-search viewer-props]
      [:div.no-search-placeholder])

    ;; MOUSE ACTIONS
    (when (and (or (seq (:selected-text viewer-props))
                   (:document-paragraph-id active-highlight))
               (seq (:provider attribution))
               (seq (:information-type attribution))
               (:expiry-date attribution))
      [:div.document-viewer--mouse-actions
       {:id "document-viewer--mouse-actions"}
       (cond
         (seq (:selected-text viewer-props))
         [evidence/document-selection viewer-props attribution false]

         (and (:document-paragraph-id active-highlight)
              (empty? selected-constructs))
         [doc-paragraphs/document-paragraphs-recommended-analysis
          {:active-highlight active-highlight
           :paragraphs paragraphs
           :is-obsolete is-obsolete
           :on-create-paragraph-evidence on-create-paragraph-evidence
           :on-create-selection-evidence on-create-selection-evidence}]

         :else
         nil)])

    [:div.pdf-viewer-wrapper
     {:ref set-pdf-ref}]
    (when-not loaded [:div.document-viewer--loader])
    [:div.document-viewer-right-sidebar
     [content-options mode set-mode set-active-highlight document-id threshold attribution on-load-paragraphs]
     (case mode
       :search
       [:div.my-1.mx-0
        [:f> doc-search/search-results viewer-props]]

       :evidence
       [:f> evidence/document-evidence-list viewer-props]

       :paragraphs
       [:f> doc-paragraphs/paragraph-recommendations
        {:all-analysis all-analysis
         :is-obsolete is-obsolete
         :on-create-paragraph-evidence on-create-paragraph-evidence
         :on-create-selection-evidence on-create-selection-evidence
         :loading-paragraphs loading-paragraphs
         :paragraphs paragraphs
         :selected-construct selected-construct
         :selected-text selected-text
         :set-active-highlight set-active-highlight
         :set-selected-construct set-selected-construct
         :set-threshold set-threshold
         :threshold threshold}]

       ; else - attribution
       [:div [:f> attribution/attribution-and-selection viewer-props]])]]))



;; ----------------------------------------------------------------------------
;;  Components
;; ----------------------------------------------------------------------------

(defn pdf-preview
  "The main controller of the PDF preview.
   Separates state from the design layer."
  [filename url organization-members document-id save-person on-prevent-close]
  (let [[loaded set-loaded] (react/useState false)
        [pdf-doc set-pdf-doc] (react/useState nil)
        [selected-text set-selected-text] (react/useState nil)
        [search-query set-search-query] (react/useState nil)
        [selection-range set-selection-range] (react/useState nil)
        [selected-constructs set-selected-constructs] (react/useState nil)
        [pdf-ref set-pdf-ref] (react/useState nil)
        [active-highlight set-active-highlight] (react/useState [])
        [threshold set-threshold] (react/useState 30)
        [has-highlight-search-result set-has-highlight-search-result] (react/useState false)

        [loading-paragraphs set-loading-paragraphs] (react/useState false)
        [saving set-saving] (react/useState false)

        doc-attribution @(doc.db/document-attribution-data* document-id)
        doc-evidence @(doc.db/document-evidence-data* document-id)
        doc-record @(doc.db/document-record* document-id)

        [attribution-state set-attribution-state] (react/useState doc-attribution)

        ; Overrides
        set-selection-range (fn [{:keys [start end] :as range}]
                              (set-selection-range
                               (cond-> range
                                 (> start end)
                                 (merge {:start end :end start}))))

        on-search-query (fn [q]
                          (when-not (:is-obsolete doc-record)
                            (on-prevent-close (nil? q))
                            (set-selected-constructs nil)
                            (set-attribution-state
                             #(assoc-in % [:reference] (doc.db/page-range->string selection-range)))
                            (set-search-query q)))
        attribution-props {:set-field (fn [& args]
                                        (set-attribution-state #(apply assoc % args)))
                           :idx 0
                           :mode :edit
                           :organization-members organization-members
                           ; favour the keyword state-evidence for the
                           ; internals of the state evidence components
                           ; attribution is a major portion of a followup
                           ; evidence entry
                           :state-evidence attribution-state
                           :validation-error nil
                           :on-save-attribution #(doc.db/set-attribution document-id attribution-state)
                           :save-person save-person}

        on-save-callback (fn [_]
                           (on-search-query nil)
                           (set-saving false))

        on-save-evidence (fn [_]
                           (when-not (:is-obsolete doc-record)
                             (set-saving true)
                             (cond
                               (= :paragraph (:type search-query))
                               (doc.db/on-create-document-evidence-from-paragraph
                                document-id
                                attribution-props
                                (:page search-query)
                                (:paragraph-id search-query)
                                (:text search-query)
                                (:html-content search-query)
                                (mapv :construct-id selected-constructs)
                                on-save-callback)

                               (= :selection (:type search-query))
                               (doc.db/on-create-document-evidence-from-selection
                                document-id
                                (:text search-query)
                                attribution-props
                                selection-range
                                selected-constructs
                                on-save-callback)

                               (= :existing-evidence (:type search-query))
                               (doc.db/on-add-analysis-to-document-evidence
                                document-id
                                attribution-state
                                selected-constructs
                                on-save-callback))))

        on-scroll-to-evidence (fn [evidence-id paragraph]
                                (set-active-highlight {:evidence-id evidence-id
                                                       :type :evidence})
                                (some-> (str "highlight-"
                                             evidence-id
                                             "-"
                                             (-> paragraph
                                                 :content-data
                                                 :page-range
                                                 :start))
                                        (->> (.getElementById js/document))
                                        .-children
                                        array-seq
                                        first
                                        (.scrollIntoView
                                         #js{:behavior "smooth"
                                             :block "center"})))
        on-cancel-evidence (fn [_] (on-search-query nil))
        ;; ---------------------
        all-analysis @(analysis.search/all-analysis-structures)
        paragraphs @(doc.db/paragraphs* document-id)
        selected-paragraph @(doc.db/selected-paragraph-sub*)
        document-search-results @(doc.db/document-search-results*)
        on-select-search-result (fn [paragraph type]
                                  (pdf/render-search-result-paragraph
                                   pdf-doc
                                   paragraph)
                                  (doc.db/set-selected-paragraph paragraph)
                                  (if (= type :content)
                                    (set-active-highlight
                                     {:document-paragraph-id (:document-paragraph-id paragraph)
                                      :type :paragraph})
                                    (set-active-highlight nil)))
        on-create-paragraph-evidence (fn [page
                                          paragraph-id
                                          paragraph-text
                                          html-content
                                          construct-ids]
                                       (when-not (:is-obsolete doc-record)
                                         (set-attribution-state #(assoc % :reference (doc.db/page-range->string page)))
                                         (on-search-query {:text paragraph-text
                                                           :type :paragraph
                                                           :page page
                                                           :paragraph-id paragraph-id
                                                           :paragraph-text paragraph-text
                                                           :html-content html-content
                                                           :construct-ids construct-ids})
                                         (set-selected-constructs (filterv (comp (set construct-ids) :construct-id) all-analysis))))
        on-create-selection-evidence (fn [selected]
                                       (when-not (:is-obsolete doc-record)
                                         (on-search-query {:text selected-text
                                                           :type :selection})
                                         (set-selected-constructs #{selected})))
        on-add-analysis-to-evidence (fn [evidence]
                                      (when-not (:is-obsolete doc-record)
                                        (set-attribution-state #(assoc evidence
                                                                       :comment ""
                                                                       :control-status nil
                                                                       :reference (-> evidence
                                                                                      :content-data
                                                                                      :page-range
                                                                                      doc.db/page-range->string)))
                                        (set-selected-constructs nil)
                                        (on-search-query {:text (:content evidence)
                                                          :type :existing-evidence
                                                          :evidence evidence})))
        on-load-paragraphs (fn [document-id threshold]
                             (set-loading-paragraphs true)
                             (doc.db/fetch-document-paragraphs document-id
                                                               threshold
                                                               #(set-loading-paragraphs false)))]

    (react/useEffect
     (fn []
       (doc.db/fetch-document-attribution document-id)
       (doc.db/fetch-document-record document-id)
       (doc.db/fetch-document-evidence document-id))
     #js [nil])

    (react/useEffect
     (fn [] (doc.db/fetch-document-evidence document-id))
     #js [all-analysis])

    (react/useEffect
     (fn [] (set-attribution-state doc-attribution))
     #js [doc-attribution])

    (react/useEffect
     (fn []
       (when (and pdf-ref (not loaded))
         (let [counter (r/atom 0)
               ;; We send the total pages to load from the
               ;; PDF reader, and count callbacks - once we
               ;; have loaded all of the pages, we set loaded
               ;; to true and process to rendering evidence
               on-load (fn [page-count]
                         (swap! counter inc)
                         (when (= @counter page-count)
                           (set-loaded true)))]
           (pdf/render-document
            {:url url
             :pdf-doc pdf-doc
             :pdf-ref pdf-ref
             :loaded loaded
             :set-pdf-doc set-pdf-doc
             :on-load on-load
             :selected-paragraph selected-paragraph
             :width 900
             :scale 1.5}))))
     (array url loaded pdf-ref))

    (react/useEffect
     (fn []
       (when (and loaded (not has-highlight-search-result))
         (set-has-highlight-search-result true)))
     (array loaded has-highlight-search-result))

    (react/useEffect
     (fn []
       (when loaded
         (pdf/clear-evidence pdf-doc doc-evidence nil)
         (pdf/render-evidence pdf-doc doc-evidence nil)))
     (array loaded doc-evidence))

    (react/useEffect
     (fn [] (pdf/render-paragraphs pdf-doc paragraphs))
     (array paragraphs))

    (react/useEffect
     (fn [] (on-load-paragraphs document-id threshold))
     (array threshold))

    (react/useEffect
     (fn []
       (when pdf-ref
        (js.events/bind-event-handlers set-selected-text
                                      set-selection-range
                                      set-active-highlight
                                      pdf-ref)))
     #js [pdf-ref search-query])

    [:div.document-viewer--wrapper
     [:div.message-header.is-highlight
      filename " - Viewer"
      (when (:is-obsolete doc-record)
        [:div.notification.is-warning.my-0.py-1
         [:h6.title.is-6
          [:i.icon.fas.fa-triangle-exclamation.mr-2]
          "Document is Obsolete"]])
      [:button.button.delete.is-info
       {:on-click #(rf/dispatch [::ui/pop-modal])}]]

     [:f> document-viewer-layout
      {:active-highlight active-highlight
       :all-analysis all-analysis
       :attribution attribution-state
       :attribution-props attribution-props
       :document-id document-id
       :document-search-results document-search-results
       :filename filename
       :highlighted doc-evidence
       :is-obsolete (:is-obsolete doc-record)
       :loaded loaded
       :loading-paragraphs loading-paragraphs

       :on-add-analysis-to-document-evidence on-add-analysis-to-evidence
       :on-cancel-evidence on-cancel-evidence
       :on-create-paragraph-evidence on-create-paragraph-evidence
       :on-create-selection-evidence on-create-selection-evidence
       :on-load-paragraphs on-load-paragraphs
       :on-save-evidence on-save-evidence
       :on-scroll-to-evidence on-scroll-to-evidence
       :on-search-query on-search-query
       :on-select-search-result on-select-search-result
       :on-set-document-search-results doc.db/set-document-search-results

       :paragraphs paragraphs
       :pdf-ref pdf-ref
       :saving saving
       :search-query search-query
       :selected-constructs selected-constructs
       :selected-paragraph selected-paragraph
       :selected-text selected-text
       :selection-range selection-range
       :threshold threshold

       :set-active-highlight set-active-highlight
       :set-pdf-ref set-pdf-ref
       :set-selected-constructs set-selected-constructs
       :set-selection-range set-selection-range
       :set-threshold set-threshold}]]))